import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "@/utils/createTheme";
import { updateFavicons } from "@/utils/faviconUtils";

const ThemeContext = createContext();

export function useThemeMode() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useThemeMode must be used within a ThemeProvider");
  }
  return context;
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    // Get saved preference from localStorage or default to 'light'
    const savedMode = localStorage.getItem("themeMode");
    return savedMode === "dark" ? "dark" : "light";
  });

  useEffect(() => {
    // Save preference to localStorage whenever it changes
    localStorage.setItem("themeMode", mode);

    // Update favicons when theme mode changes
    const brandName = import.meta.env.VITE_BRAND || "brytebridge";
    updateFavicons(brandName, mode);
  }, [mode]);

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const value = useMemo(
    () => ({
      mode,
      toggleColorMode,
    }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
